import { authenticateInviteMagicLink, generateJWTTokenFromInitialAuth, getInitialOrganizationListFromAuth, getMemberDetailsWithOrganization, switchOrganization } from "@/modules/auth/requests";
import { LOGIN_STEP } from "@/modules/auth/types";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import { setCookie } from "@/shared/utils/cookie-helper";
import parsePromise from "@/shared/utils/parsePromise";
import ZaniaLogoLoader from "@components/page-loader";
import { useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthActions } from "../../states";
import { useLoginActions } from "../../states/login";
import { authenticateSso } from "../../use-cases/sso.use-case";
import { authChannel } from "@/infra/auth/broadcast";

const Authenticate = () => {
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const tokenType = searchParams.get("stytch_token_type");
  const { setAuth } = useAuthActions();
  const navigate = useNavigate();
  const {
    setLoginStep,
    setDiscoveredOrganizations,
  } = useLoginActions();

  const completeAuthWithMember = useCallback(async () => {
    const [error, memberData] = await parsePromise(getMemberDetailsWithOrganization());

    if (memberData?.member) {
      setAuth({
        isAuthenticated: true,
        isAuthenticationInProgress: false,
        loggedInMember: memberData.member,
        loggedInOrganization: memberData.organization,
      });
      authChannel.postMessage("loggedIn");
      return true;
    }

    // Fallback: set authenticated without member (will be fetched by App.tsx)
    setAuth({
      isAuthenticated: true,
      isAuthenticationInProgress: false,
    });
    authChannel.postMessage("loggedIn");
    return false;
  }, [setAuth]);

  useEffect(() => {
    setLoginStep(LOGIN_STEP.AUTHENTICATING);
  }, []);

  let count = 0;
  const authenticate = useCallback(async () => {

    if (token && tokenType) {
      // Prevent multiple calls
      count += 1;
      if (count > 1) {
        return;
      }

      switch (tokenType) {
        case "discovery":
        case "discovery_oauth": {
          const [error, data] = await parsePromise(getInitialOrganizationListFromAuth(tokenType === "discovery_oauth" ? "discover_oauth" : tokenType, token));
          if (error) {
            addNotification({
              type: "error",
              title: "Error",
              message: error?.message,
            });
            setLoginStep(LOGIN_STEP.MAIN);
            navigate(`/${ROUTES.LOGIN}`);
            return;
          }
          
          // Store auth method for OAuth flows (Google/Microsoft)
          if (tokenType === "discovery_oauth") {
            setCookie("sso_auth_method", "email", 30);
          }
          
          if (data && data?.intermediate_session_token) {
            document.cookie = `intermediate_session_token=${data.intermediate_session_token};path=/`
          }

          if (data?.discovered_organizations?.length && data?.discovered_organizations?.length > 1) {
            localStorage.setItem("discoveredOrgs", JSON.stringify(data?.discovered_organizations));
            setDiscoveredOrganizations(data.discovered_organizations);
            setLoginStep(LOGIN_STEP.SELECT_ORG);
            navigate(`/${ROUTES.LOGIN}`);
            authChannel.postMessage("selectOrganization");
          } else if (data?.discovered_organizations?.length && data?.discovered_organizations?.length === 1) {
            const [error, resp] = await parsePromise(generateJWTTokenFromInitialAuth(data.discovered_organizations[0].organization.organization_id, data.intermediate_session_token));
            if (error) {
              addNotification({
                type: "error",
                title: "Error",
                message: error?.message,
              });
              setLoginStep(LOGIN_STEP.MAIN);
              navigate(`/${ROUTES.LOGIN}`);
              return;
            } else {
              await completeAuthWithMember();
              setLoginStep(LOGIN_STEP.MAIN);
              navigate(`/${ROUTES.AGENT}`);
            }
          } else {
            addNotification({
              type: "error",
              title: "No organizations found",
              message: "Please contact support@zania.ai to get access to an organization",
            });
            setLoginStep(LOGIN_STEP.MAIN);
            navigate(`/${ROUTES.LOGIN}`);
          }          

          break;
        }
        case "multi_tenant_magic_links": {
          const [error, data] = await parsePromise(authenticateInviteMagicLink(token));
          if (error) {
            addNotification({
              type: "error",
              title: "Error",
              message: error?.message,
            });
            setLoginStep(LOGIN_STEP.MAIN);
            navigate(`/${ROUTES.LOGIN}`);
            return;
          }
          else {
            // Store auth type as email in cookie with 30 days expiry
            setCookie("sso_auth_method", "email", 30);
            document.cookie = `intermediate_session_token=${token};path=/`
            await completeAuthWithMember();
            setLoginStep(LOGIN_STEP.MAIN);
            navigate(`/${ROUTES.AGENT}`)
          }
          break;
        }
        case "sso": {
          const [error, data] = await parsePromise(authenticateSso(token));
          if (error) {
            addNotification({
              type: "error",
              title: "Error",
              message: error?.message,
            });
            setLoginStep(LOGIN_STEP.MAIN);
            navigate(`/${ROUTES.LOGIN}`);
          }
          else if (data) {
            // Store auth type as sso in cookie with 30 days expiry
            setCookie("sso_auth_method", "sso", 30);
            document.cookie = `intermediate_session_token=${token};path=/`
            
            // Combine parent and children organizations into discovered organizations format
            const allOrganizations = [
              data.parent_organization,
              ...data.children_organizations
            ];

            const discoveredOrgs = allOrganizations.map(org => ({
              member_authenticated: false,
              organization: {
                organization_id: org.organization_id,
                organization_name: org.organization_name,
                organization_logo_url: '',
                organization_slug: '',
                sso_jit_provisioning: '',
                sso_jit_provisioning_allowed_connections: [],
                sso_active_connections: [],
                email_allowed_domains: [],
                email_jit_provisioning: '',
                email_invites: '',
                auth_methods: '',
                allowed_auth_methods: [],
                mfa_policy: '',
                rbac_email_implicit_role_assignments: [],
                mfa_methods: '',
                allowed_mfa_methods: [],
                scim_active_connections: [],
                trusted_metadata: {},
                sso_default_connection_id: null
              },
              membership: {
                type: 'direct_member',
                details: null,
                member: data.member
              },
              primary_required: null,
              mfa_required: null
            }));
            
            localStorage.setItem("discoveredOrgs", JSON.stringify(discoveredOrgs));
            
            if (discoveredOrgs.length > 1) {
              setDiscoveredOrganizations(discoveredOrgs);
              setLoginStep(LOGIN_STEP.SELECT_ORG);
              navigate(`/${ROUTES.LOGIN}`);
            } else if (discoveredOrgs.length === 1) {
              // If there's only one organization, use switch-org API (same as multi-org flow)
              const [error, resp] = await parsePromise(
                switchOrganization(discoveredOrgs[0].organization.organization_id)
              );
              
              if (error) {
                addNotification({
                  type: "error",
                  title: "Error",
                  message: error?.message,
                });
                setLoginStep(LOGIN_STEP.MAIN);
                navigate(`/${ROUTES.LOGIN}`);
              } else {
                await completeAuthWithMember();
                setLoginStep(LOGIN_STEP.MAIN);
                navigate(`/${ROUTES.AGENT}`);
              }
            } else {
              addNotification({
                type: "error",
                title: "No organizations found",
                message: "No organizations were found for this SSO account",
              });
              setLoginStep(LOGIN_STEP.MAIN);
              navigate(`/${ROUTES.LOGIN}`);
            }
          }
          break;
        }
        default:
          break;
      }
    } else {
      navigate(`/${ROUTES.LOGIN}`);
    }
  }, [token, tokenType, navigate, setLoginStep, setAuth]);

  useEffect(() => {
    authenticate();
  }, [authenticate]);

  return (
    <div className="flex items-center justify-center h-screen">
      <ZaniaLogoLoader />
    </div>
  )
}

export default Authenticate;